import { IconButton, Tag, ViewPlaceholder } from '@vrplatform/components';
import {
  captureSentryError,
  showApiErrorNotification,
  useApiMutation,
  useApiQuery,
  useInvalidateApiQueries,
  useQueryClient,
} from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import type { MRT_ColumnDef } from '@vrplatform/table';
import { Drawer } from '@vrplatform/components-legacy';
import { showWarnNotification } from '@vrplatform/theme';
import { type Maybe, day, utc } from '@vrplatform/utils';
import { Box, Center, Group, LoadingOverlay, Stack, Text } from '@mantine/core';
import {
  formatAddress,
  formatOwnerName,
  formatPercentage,
} from '@vrplatform/ui-common';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { LedgerStatementStatusBadge } from '../../views/statements/_components';
import { DrawerCollapsableTable, DrawerHeader } from '../_components';
import { OwnerEditForm, type OwnerEditFormInputs } from './OwnerEditForm';
import { OwnerEllipsisMenuItems } from './_components/OwnerEllipsisMenuItems';
import { OwnerEllipsisMenuModals } from './_components/OwnerEllipsisMenuModals';
import { OwnerInfoCard } from './_components/OwnerInfoCard';
import { useOwnerDetailDrawer } from './useOwnerDetailDrawer';
import {
  type OwnerDetail,
  useOwnerDetailDrawerQuery,
} from './useOwnerDetailDrawerQuery';
import { useListingDetailDrawer } from '../../views/listings/drawer';
import {
  type OwnersTableQueryData,
  useOwnersTableQuery,
} from '../../views/owners/ledger/list/useOwnersTableQuery';

export const STORYBOOK_OWNER_DETAIL_DRAWER_STATEMENTS_QUERY_KEY =
  'owner-detail-drawer-statements';

function useGotoDetail() {
  const goto = useNavigate();

  return useCallback(
    (ownerId: string, view: 'overview' | 'statements' = 'overview') => {
      goto(
        `/owner/${ownerId}${view === 'statements' ? '/owner-statements' : ''}`
      );
    },
    [goto]
  );
}

export const OwnerDetailDrawer = () => {
  const {
    opened,
    close,
    ownerId,
    view,
    setView,
    isReview,
    shouldAutoAdvanceReview,
    openReview,
  } = useOwnerDetailDrawer();
  const queryClient = useQueryClient();

  const { queryKey: reviewQueryKey } = useOwnersTableQuery({
    type: 'in-review',
    skip: true,
    refetchOnMount: false,
  });

  const gotoDetail = useGotoDetail();

  const [modalOpened, setModalOpened] = useState<{
    modal: 'delete' | 'archive' | 'email' | 'copy-link' | 'revoke-access';
    owner: OwnerDetail;
  } | null>(null);

  const {
    isLoading,
    data: owner,
    isFetching,
    error: ownerError,
  } = useOwnerDetailDrawerQuery(ownerId);

  const invalidate = useInvalidateApiQueries(['owners']);

  const { mutateAsync } = useApiMutation('put', '/contacts/{id}');

  const getNextReviewOwnerId = useCallback(() => {
    if (!ownerId) return undefined;

    const cachedData =
      queryClient.getQueryData<OwnersTableQueryData>(reviewQueryKey);
    const owners = cachedData?.pages.flatMap((page) => page.list) ?? [];

    if (!owners.length) return undefined;

    const currentIndex = owners.findIndex((owner) => owner.id === ownerId);

    if (currentIndex >= 0 && currentIndex < owners.length - 1) {
      return owners[currentIndex + 1]?.id;
    }
    if (currentIndex >= 0) {
      return undefined;
    }

    return undefined;
  }, [ownerId, queryClient, reviewQueryKey]);

  const resetView = useCallback(() => {
    const path = window.location.pathname;

    if (path.startsWith('/owner/')) {
      close();
    } else {
      setView('overview');
    }
  }, [close, setView]);

  const handleUpdate = useCallback(
    async (formData: OwnerEditFormInputs) => {
      if (!ownerId) {
        captureSentryError(new Error('No owner id found when updating owner'));

        return showWarnNotification({
          message: 'No owner found',
        });
      }
      await mutateAsync({
        params: {
          path: {
            id: ownerId,
          },
        },
        body: {
          payoutAccountId: formData.defaultAccountId || null,
          email: formData.email?.trim() || null,
          phone: formData.phone?.trim() || null,
          companyType: formData.type === 'individual' ? null : formData.type,
          status: isReview ? 'active' : undefined,
          firstName:
            formData.type === 'individual'
              ? formData.firstName?.trim() || null
              : null,
          name: formData.lastName?.trim() || null,
          taxIdentifier: formData.taxId?.trim() || null,
          address:
            formData.address.countryCode &&
            formData.address.stateCode &&
            formData.address.line1?.trim()
              ? {
                  city: formData.address.city.trim(),
                  line1: formData.address.line1.trim(),
                  line2: formData.address.line2?.trim() || null,
                  stateCode: formData.address.stateCode,
                  countryCode: formData.address.countryCode,
                  postalCode: formData.address.postcode.trim(),
                  full: formatAddress(formData.address),
                }
              : null,
        },
      })
        .then(() => {
          invalidate();

          if (!isReview) {
            resetView();
            return;
          }

          if (!shouldAutoAdvanceReview) {
            close();
            return;
          }

          const nextOwnerId = getNextReviewOwnerId();

          if (nextOwnerId) {
            openReview(nextOwnerId, 'edit', {
              history: 'replace',
            });
            return;
          }

          close();
        })
        .catch((error) => {
          showApiErrorNotification({
            error,
            title: "We couldn't update the owner",
          });
        });
    },
    [
      close,
      getNextReviewOwnerId,
      invalidate,
      isReview,
      mutateAsync,
      openReview,
      ownerId,
      resetView,
      shouldAutoAdvanceReview,
    ]
  );

  const closeModal = useCallback(() => setModalOpened(null), []);

  const showMenuItems = view === 'overview' && owner;

  return (
    <>
      {ownerId && (
        <OwnerEllipsisMenuModals
          owner={owner}
          deleteModal={{
            closeModal,
            opened: modalOpened?.modal === 'delete' && !!modalOpened?.owner,
            onSuccess: close,
          }}
          archiveModal={{
            closeModal,
            opened: modalOpened?.modal === 'archive' && !!modalOpened?.owner,
          }}
          emailModal={{
            closeModal,
            opened: modalOpened?.modal === 'email' && !!modalOpened?.owner,
          }}
          copyLinkModal={{
            closeModal,
            opened: modalOpened?.modal === 'copy-link' && !!modalOpened?.owner,
          }}
          revokeAccessModal={{
            closeModal,
            opened:
              modalOpened?.modal === 'revoke-access' && !!modalOpened?.owner,
          }}
        />
      )}
      <Drawer opened={opened} onClose={close} size={550}>
        <DrawerHeader
          closeDrawer={close}
          title={
            isReview ? (
              'Review owner'
            ) : (
              <Group gap="xs" wrap="nowrap">
                <Icon
                  icon={owner?.type === 'company' ? 'OfficeIcon' : 'UserIcon'}
                  size={22}
                  color="gray"
                />
                <Text size="xl" fw={500} component="h3" m={0}>
                  {formatOwnerName(owner || undefined, {
                    lastNameFirst: false,
                    showEmpty: true,
                  })}
                </Text>
              </Group>
            )
          }
          type={isReview ? undefined : 'Owner'}
          loading={isLoading}
          menuItems={
            showMenuItems && (
              <OwnerEllipsisMenuItems
                openDeleteModal={() =>
                  setModalOpened({
                    modal: 'delete',
                    owner,
                  })
                }
                openArchiveModal={() =>
                  setModalOpened({
                    modal: 'archive',
                    owner,
                  })
                }
                openEmailModal={() =>
                  setModalOpened({
                    modal: 'email',
                    owner,
                  })
                }
                openCopyLinkModal={() =>
                  setModalOpened({
                    modal: 'copy-link',
                    owner,
                  })
                }
                openRevokeAccessModal={() =>
                  setModalOpened({
                    modal: 'revoke-access',
                    owner,
                  })
                }
                contact={owner}
              />
            )
          }
          containerSx={
            view === 'edit'
              ? {
                  marginBottom: 0,
                }
              : undefined
          }
        >
          {showMenuItems && (
            <IconButton
              onClick={(event) => {
                event.stopPropagation();
                gotoDetail(owner.id);
              }}
              variant="outline"
              icon="ArrowRightCircleIcon"
              tooltip="Go to detail"
            />
          )}
        </DrawerHeader>

        <LoadingOverlay
          visible={isFetching}
          loaderProps={{
            size: 'sm',
          }}
        />

        {ownerError ? (
          <ViewPlaceholder
            title={ownerError?.message || 'Failed to load owner'}
            description="Please try again and contact our customer support if the issue persists."
            icon="AlertTriangleIcon"
            iconColor="orange"
            backgroundColor="transparent"
            contentMaxWidth={300}
          />
        ) : !owner && !isLoading ? (
          <ViewPlaceholder
            title="No owner found"
            description="Has the owner been deleted?"
            icon="CommentQuestionIcon"
            backgroundColor="transparent"
          />
        ) : view === 'edit' && owner ? (
          <OwnerEditForm
            isOwnerAddModal={false}
            handleSubmit={handleUpdate}
            initialValues={{
              firstName: owner.firstName || '',
              lastName: owner.name || '',
              email: owner.email!,
              defaultAccountId: owner.defaultAccountId ?? null,
              phone: owner.phone || '',
              taxId: owner.taxId || '',
              type:
                owner.type === 'individual'
                  ? 'individual'
                  : owner.companyType?.value || 'individual',
              address: owner.address,
              addUserOption: false,
            }}
            onReset={resetView}
            submitButtonLabel={
              shouldAutoAdvanceReview ? 'Save and continue' : 'Save changes'
            }
            isEmailDisabled={!!owner.userAccess?.userId}
          />
        ) : (
          <Content owner={owner} />
        )}
      </Drawer>
    </>
  );
};

const Content = ({ owner }: { owner: Maybe<OwnerDetail> }) => {
  if (!owner) return null;

  return (
    <Stack
      gap={'md'}
      mb="md"
      sx={{
        position: 'relative',
      }}
    >
      <OwnerInfoCard
        owner={{
          ...owner,
          companyType: owner.companyType?.label,
        }}
        defaultPayoutAccountName={owner.defaultPayoutAccountName}
      />

      <ListingPeriod memberships={owner.currentPeriodMemberships} />

      <OwnerStatements ownerId={owner.id} />
    </Stack>
  );
};

const ListingPeriod = ({
  memberships: rows,
}: {
  memberships: OwnerDetail['currentPeriodMemberships'];
}) => {
  const { open: openListingDetailDrawer } = useListingDetailDrawer();

  return (
    <DrawerCollapsableTable
      title="Current Ownerships"
      rightSection={null}
      rowData={rows}
      defaultOpened
      columns={[
        {
          header: 'Owner',
          accessorKey: 'id',
          Cell: ({ row }) => {
            const member = row.original;

            return (
              <Tag
                key={member.id}
                leftIcon="HomeIcon"
                leftIconColor={(theme) => theme.colors[theme.primaryColor][6]}
              >
                {member.listingName}
              </Tag>
            );
          },
        },
        {
          header: 'Split',
          accessorKey: 'split',
          maxSize: 0,
          minSize: 100,
          size: 100,
          mantineTableBodyCellProps: {
            align: 'right',
          },
          Cell: ({ row }) => {
            const data = row.original;
            return formatPercentage(data.split || 0, 'super-base-points');
          },
        },
      ]}
      emptyRowsFallback={() => (
        <Center>
          <Text size="sm" c="gray">
            No current ownerships
          </Text>
        </Center>
      )}
      onRowClick={{
        handler: (row) => openListingDetailDrawer(row.original.listingId),
      }}
    />
  );
};

const OwnerStatements = ({ ownerId }: { ownerId: string }) => {
  const goto = useNavigate();
  const gotoOwnerDetail = useGotoDetail();
  // const [{ statementStartAt }] = useTeam();
  //  const dateRange = `${statementStartAt ? day(statementStartAt).startOf('month').yyyymmdd() : day().startOf('year').yyyymmdd()}...${day().endOf('month').yyyymmdd()}`;

  const year = day().year();

  const {
    data: ownerStatements = [],
    error,
    isLoading: isInitialLoading,
  } = useApiQuery(
    async (api, signal, args) => {
      const result = await api.GET('/statements', {
        params: {
          query: {
            ownerIds: args.ownerId,
            year: args.year,
          },
        },
        signal,
      });

      if (result.error) throw result.error;

      return (result.data?.data || []).map((statement) => ({
        ...statement,
        id: statement.id ?? statement.uniqueRef,
      }));
    },
    {
      queryGroup: 'owners',
      uniqueQueryKey: 'owner-detail-drawer',
      variables: {
        ownerId,
        year,
      },
      queryKeys: ['owner-statements'],
      hideNotification: true,
      storybookQueryKey: STORYBOOK_OWNER_DETAIL_DRAWER_STATEMENTS_QUERY_KEY,
    }
  );

  const columns = useMemo<MRT_ColumnDef<(typeof ownerStatements)[number]>[]>(
    () => [
      {
        header: 'Owner',
        accessorKey: 'id',
        Cell: ({ row }) => {
          const statement = row.original;

          return (
            <Box>
              <Text component="span" display="block" size="sm">
                {utc(statement.startAt).format('MMM YYYY')}
              </Text>
              {statement.listing.name && (
                <Text component="span" display="block" size="xs" color="gray">
                  {statement.listing.name}
                </Text>
              )}
            </Box>
          );
        },
      },
      {
        header: 'Status',
        accessorKey: 'status',
        maxSize: 150,
        mantineTableBodyCellProps: {
          align: 'right',
        },
        Cell: ({ row }) => {
          const statement = row.original;

          return (
            <LedgerStatementStatusBadge
              disabled
              status={statement.status}
              opened={false}
            />
          );
        },
      },
    ],
    []
  );

  return (
    <DrawerCollapsableTable
      title={`Owner Statements - ${year}`}
      paginationSize={15}
      rightSection={
        <IconButton
          tooltip="View all statements"
          icon="ArrowRightIcon"
          onClick={() => gotoOwnerDetail(ownerId, 'statements')}
          iconSize={16}
        />
      }
      rowData={error ? [] : ownerStatements}
      columns={columns}
      loading={isInitialLoading}
      defaultOpened={!!error || !!ownerStatements.length}
      onRowClick={{
        handler: ({ original }) => {
          original.ownership &&
            goto(
              `/statement/${original.ownership.id}?date=${utc(
                original.startAt
              ).format('YYYY-MM-01')}&currency=${original.currency}`
            );
        },
      }}
      emptyRowsFallback={() => {
        if (error) {
          return (
            <Center
              sx={{
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <Icon
                icon="AlertTriangleIcon"
                color={(theme) => theme.colors.red[6]}
                size={18}
              />
              <Text size="sm" c="red">
                {error.message}
              </Text>
            </Center>
          );
        }

        return (
          <Center>
            <Text size="sm" c="gray">
              No owner statements
            </Text>
          </Center>
        );
      }}
    />
  );
};
